import { computedWithControl, createGlobalState } from "@vueuse/core";
import { useDoc } from "addeus-common-library/stores/firestore";
import { useUserSession } from "addeus-common-library/stores/userSession";
import { watch } from "vue";
import { setUser } from "../error-handling";
import { UserMetadata } from "../models/userMetadata";
import { useRouter } from "vue-router";
import LogRocket from "logrocket";

export const LAST_ACTIVITY_KEY = "client_last_activity_sur_addeus";
export const INACTIVITY_TIME = 1000;
export type UserData = Record<string, any> | null;


function startUserActivityTracking(onInactivityReset, timeInMinute: number): () => void {
    const resetActivity = () => {
        localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().valueOf().toString());
    };
  
    resetActivity();

    window.addEventListener("mousemove", resetActivity);
    window.addEventListener("keydown", resetActivity);
    window.addEventListener("click", resetActivity);
    window.addEventListener("scroll", resetActivity);

    const intervalId = setInterval(() => {
        const timeInString = localStorage.getItem(LAST_ACTIVITY_KEY);
        if (timeInString === undefined || timeInString === null) {
            resetActivity(); // Set timestamp if missing
            return;
        }

        const lastActivityTime = parseInt(timeInString);
        const currentTime = Date.now().valueOf();
        const inactiveMinutes = (currentTime - lastActivityTime) / 60000;

        // Check if user has been inactive for the specified time
        if (inactiveMinutes >= timeInMinute) {
            onInactivityReset();
            // Reset the timer after triggering the callback
            resetActivity();
        }
    }, 60000);

    // Return cleanup function
    return () => {
        window.removeEventListener("mousemove", resetActivity);
        window.removeEventListener("keydown", resetActivity);
        window.removeEventListener("click", resetActivity);
        window.removeEventListener("scroll", resetActivity);
        clearInterval(intervalId);
    };
}

export const useUserMetadataStore = createGlobalState(() => {
    const userSession = useUserSession();
    const router = useRouter();

    const currentUserMetadata = computedWithControl(
        () => {
            if (userSession.user === null) return null;
            return (userSession.user as any).uid;
        },
        () => {
            if (userSession.user === null) return null;
            return useDoc(UserMetadata, (userSession.user as any).uid);
        },
    );

    watch(
        currentUserMetadata,
        async () => {
            if (currentUserMetadata.value !== null) {
                LogRocket.identify(currentUserMetadata.value.$getID() || "", {
                    name: currentUserMetadata.value.toString(),
                    email: currentUserMetadata.value.email,
                });
                setUser({
                    email: currentUserMetadata.value.email,
                });
            } else setUser(null);
        },
        {
            immediate: true,
        },
    );

    return {
        currentUserMetadata,
        startUserActivityTracking,
        fetched: () =>
            currentUserMetadata.value !== null
                ? currentUserMetadata.value.$getMetadata().refresh()
                : Promise.reject(new Error("user not logged in")),
        isLoggedIn() {
            return userSession.isLoggedIn;
        },
    };
});